import formatDateOnly from '~support-at-home/lib/formatDateOnly';

import { Column } from '~components/table-view';
import { genericCellRender } from '~components/table-view/columnRenderers';
import TruncatedText from '~support-at-home/components/config/contributions/TruncatedText';

/**
 * Column definitions for the Services Australia contributions import result tables (TAV-3421).
 *
 * Dates here are bare calendar dates (YYYY-MM-DD), so they go through formatDateOnly rather than a
 * datetime parser, which would shift the day.
 */

const period: Column = {
  id: 'period',
  header: 'Period',
  type: 'text',
  // Spelled out rather than formatDateOnly's dash placeholder: a rate with no end date is a normal,
  // open-ended rate and should read as one.
  deriveValue: (row) =>
    `${row.start_date ? formatDateOnly(row.start_date) : 'no start date'} - ${
      row.end_date ? formatDateOnly(row.end_date) : 'no end date'
    }`,
};

const formatRate = (value: unknown): string => (value === null || value === undefined ? '' : `${value}%`);

// Created / Updated: contribution rates the import actually wrote.
export const importItemColumns: Column[] = [
  {
    id: 'client_name',
    header: 'Client',
    type: 'text',
  },
  {
    id: 'category_name',
    header: 'Category',
    type: 'text',
  },
  {
    id: 'funding_source_name',
    header: 'Budget type',
    type: 'text',
  },
  {
    id: 'contrib_proportion',
    header: 'Rate',
    type: 'text',
    deriveValue: (row) => formatRate(row.contrib_proportion),
  },
  period,
];

// Skipped: contribution rates the import fetched but could not write.
export const importSkippedColumns: Column[] = [
  {
    id: 'client_name',
    header: 'Client',
    type: 'text',
    // A skipped rate usually has no matching client, so name the care recipient Services Australia
    // sent instead.
    deriveValue: (row) => {
      if (row.client_name) return row.client_name;
      if (row.care_recipient_id) return `Care recipient ${row.care_recipient_id}`;
      return 'Unmatched';
    },
  },
  {
    id: 'category_name',
    header: 'Category',
    type: 'text',
    deriveValue: (row) => row.category_name ?? 'Unknown',
  },
  {
    id: 'contribution_type',
    header: 'Budget type',
    type: 'text',
    deriveValue: (row) => row.contribution_type ?? 'Unknown',
  },
  {
    id: 'contrib_proportion',
    header: 'Rate',
    type: 'text',
    deriveValue: (row) => formatRate(row.contrib_proportion),
  },
  period,
  {
    id: 'reason',
    header: 'Reason',
    type: 'text',
    // One line with the full text on hover. The result table sits in a box that scrolls on its own.
    renderReadOnly: genericCellRender((_value, row) => TruncatedText({ text: row?.reason ?? '' })),
  },
];
